/**
 * Shared section spacing utility.
 *
 * Every block should read `block.sectionSpacing` and pass it here
 * so spacing is consistent site-wide yet editable per-block from the CMS.
 */

const SECTION_TOP_SPACING = {
	none: '',
	xs: 'pt-2 md:pt-4',
	sm: 'pt-4 md:pt-6',
	md: 'pt-8 md:pt-12',
	lg: 'pt-16 md:pt-20',
	xl: 'pt-20 lg:pt-24',
} as const;

const SECTION_COMPACT_TOP_SPACING: Record<keyof typeof SECTION_TOP_SPACING, string> = {
	none: '',
	xs: 'pt-0',
	sm: 'pt-2 md:pt-4',
	md: 'pt-4 md:pt-6',
	lg: 'pt-8 md:pt-12',
	xl: 'pt-16 md:pt-20',
};

const SECTION_BOTTOM_SPACING: Record<keyof typeof SECTION_TOP_SPACING, string> = {
	none: '',
	xs: 'pb-2 md:pb-4',
	sm: 'pb-4 md:pb-6',
	md: 'pb-8 md:pb-12',
	lg: 'pb-16 md:pb-20',
	xl: 'pb-20 lg:pb-24',
};

const DEFAULT_SPACING = 'md';

function resolveSpacing(spacing?: string): SectionSpacing {
	if (spacing && spacing in SECTION_TOP_SPACING) {
		return spacing as SectionSpacing;
	}

	return DEFAULT_SPACING;
}

/**
 * Returns the Tailwind padding classes for a section's vertical spacing.
 *
 * @param spacing - One of 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'.
 *                  Falls back to 'md' when undefined or unrecognised.
 * @param options.hasHeader - Keeps the standard top padding when the block
 *                            renders a section header. When false, the top
 *                            padding is reduced while the bottom padding stays
 *                            unchanged.
 */
export function getSectionSpacing(spacing?: string, options?: { hasHeader?: boolean }): string {
	const resolvedSpacing = resolveSpacing(spacing);
	const topSpacing =
		options?.hasHeader === false
			? SECTION_COMPACT_TOP_SPACING[resolvedSpacing]
			: SECTION_TOP_SPACING[resolvedSpacing];

	return [topSpacing, SECTION_BOTTOM_SPACING[resolvedSpacing]].filter(Boolean).join(' ');
}

export type SectionSpacing = keyof typeof SECTION_TOP_SPACING;
